import { useMemo, useState } from "react"
import type { CustomField } from "@/schema"
import type { SearchResult } from "@/schema/search"
import { BaseInlineInput } from "@ui/forms/inline"
import { Button } from "@ui/shad"
import { LoaderCircle, PenSquare, X } from "lucide-react"

import { CrossReferenceChip } from "@/components/cross-references/cross-reference-chip"
import { ConfirmDialog } from "@/components/dialogs"
import { SearchDialog } from "@/components/dialogs/search-dialog"
import PeekView from "@/components/peek-view"

import { allowedRelationshipSearchTypes, SEARCH_TYPE_TO_BACKEND_TYPE } from "./relationship-targets"
import { getCrossReferenceData, type CrossReferenceValue } from "./types"

interface CrossReferenceInlineFieldProps {
  customField: CustomField
  value: CrossReferenceValue | null
  onSave: (value: unknown) => void
  isLoading: boolean
  label?: string | React.ReactNode
  fixedLabelWidth?: string
}

export function CrossReferenceInlineField({
  customField,
  value,
  onSave,
  isLoading,
  label,
  fixedLabelWidth,
}: CrossReferenceInlineFieldProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const inlinePlaceholder = `Set ${customField.display_name.toLowerCase()}...`

  const currentValue = value ? getCrossReferenceData(value) : null

  const allowedSearchTypes = useMemo(() => allowedRelationshipSearchTypes(customField), [customField])

  const handleSelect = (item: SearchResult) => {
    const backendType = SEARCH_TYPE_TO_BACKEND_TYPE[item.type] ?? "Household"
    onSave({ type: backendType, id: item.id, label: item.display_label })
    setIsDialogOpen(false)
  }

  const handleClear = () => {
    onSave(null)
    setIsDialogOpen(false)
  }

  const displayLabel = currentValue?.label ?? (currentValue ? `${currentValue.type} ${currentValue.id}` : "")
  const peekRecordType = currentValue?.type

  const content = (
    <div className="flex w-full items-center">
      {currentValue && peekRecordType ? (
        <div className="inline-flex min-h-[32px] flex-1 items-center gap-2 px-2">
          <PeekView
            label={displayLabel}
            recordType={peekRecordType as "Client" | "Household" | "User" | "Prospect" | "Meeting"}
            recordId={String(currentValue.id)}
            size="sm"
            className="truncate"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          className="hover:bg-muted inline-flex min-h-[32px] flex-1 items-center gap-2 rounded-md px-2 text-left text-sm"
          disabled={isLoading}
        >
          {currentValue ? (
            <CrossReferenceChip
              value={{ type: currentValue.type!, id: Number(currentValue.id!), label: currentValue.label }}
              size="sm"
              variant="pill"
            />
          ) : (
            <span className="text-input-placeholder truncate">{inlinePlaceholder}</span>
          )}
        </button>
      )}
      <div className="flex items-center gap-1 pl-2">
        {currentValue && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="iconXs"
              aria-label="Change reference"
              onClick={() => setIsDialogOpen(true)}
              disabled={isLoading}
            >
              <PenSquare className="size-4" strokeWidth={1.5} />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="iconXs"
              aria-label="Clear reference"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading}
            >
              <X className="size-4" strokeWidth={1.5} />
            </Button>
          </>
        )}
        {isLoading && <LoaderCircle className="h-4 w-4 animate-spin" />}
      </div>
    </div>
  )

  return (
    <>
      {label !== undefined ? (
        <BaseInlineInput label={label} fixedLabelWidth={fixedLabelWidth}>
          {content}
        </BaseInlineInput>
      ) : (
        content
      )}

      <SearchDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSelect={handleSelect}
        title="Select Reference"
        placeholder={"Search..."}
        allowedTypes={allowedSearchTypes}
        showPreview={true}
        widthVariant="compact"
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={`Clear ${customField.display_name.toLowerCase()}?`}
        description={`Are you sure you want to clear ${displayLabel || "this reference"}?`}
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowDeleteConfirm(false) }}
        action={{
          label: "Clear",
          onClick: () => {
            handleClear()
            setShowDeleteConfirm(false)
          },
        }}
      />
    </>
  )
}
